// The submit page's search half: look an identifier up before deciding whether
// to run anything.
//
// Two answers, kept apart on purpose. What THIS deployment observed when it
// detonated the file, and what somebody else reported about the identifier
// earlier, somewhere else. They are different kinds of claim and the panel
// never merges them into one score - a feed record is a sighting, not a
// verdict, so it wears the accent and the local verdict wears the verdict
// colours it earned.
//
// The order is the feature. A file this deployment has already analysed is
// answered from its own report and the feed is never asked: our own detonation
// is the better answer, and not asking also stops us telling a third party we
// hold the sample.

import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowRight, Download, Loader2, Search, Zap } from 'lucide-react';
import { useMemo, useState } from 'react';
import { AnalysedHere } from '@/components/intel/AnalysedHere';
import { IntelSearchIcon } from '@/components/intel/IntelSearchIcon';
import {
  intelHeadline,
  intelKindLabel,
  intelSources,
  intelVerdictLine,
  mergedFacts,
  skipReasonOf,
} from '@/components/intel/intelSearch';
import { CopyButton } from '@/components/report/CopyButton';
import type { IntelSourceResult } from '@/components/report/intelIndex';
import { useToast } from '@/context/ToastContext';
import type { IntelLocalAnalysis, IntelSearchResponse, StagedUpload } from '@/lib/api';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/classnames';

/** The measure the hero copy, its field and the upload tab's hero all share.
 *  Sized apart they wrap differently and read as a misalignment. */
export const HERO_WIDTH = '620px';

const HEADLINE_TONE = {
  reported: 'border-accent-500/40 bg-accent-500/[0.08]',
  neutral: 'border-ink-800',
  warning: 'border-verdict-suspicious/40 bg-verdict-suspicious/[0.07]',
} as const;


export function IntelSearchPanel({
  orgId,
  term,
  onTerm,
  onStaged,
  acquireSlot,
}: {
  orgId: string;
  /** The submitted term, held by the page so it survives a tab switch and can
   *  be linked to. Empty shows the hero. */
  term: string;
  onTerm: (value: string) => void;
  /** Called with the staged upload a detonation produced, plus the slot ticket
   *  and org it started under: the page owns the one staged slot and refuses
   *  an acquisition a newer one has superseded. The page switches to the
   *  upload flow, which owns everything after this point. */
  onStaged: (upload: StagedUpload, acquisition: { slot: number; orgId: string }) => void;
  /** Mints a slot ticket. Called when a fetch starts, not when it lands -
   *  that is what lets the page refuse a stale arrival in either order. */
  acquireSlot: () => number;
}) {
  const toast = useToast();
  const [draft, setDraft] = useState(term);
  // The term can change without this component remounting: a back/forward
  // navigation rewrites the URL the page reads it from. Adjusting during
  // render (React's documented pattern for a prop-derived value) is what keeps
  // the box showing the search that is actually on screen; an effect would
  // paint the stale one for a frame first.
  const [shownTerm, setShownTerm] = useState(term);
  if (term !== shownTerm) {
    setShownTerm(term);
    setDraft(term);
  }

  const searchQ = useQuery({
    queryKey: ['intel-search', orgId, term],
    queryFn: () => api.intelSearch(orgId, term),
    enabled: term !== '',
    // An identifier's reputation does not change while an analyst reads the
    // card, and every refetch is a metered third-party call. Nothing here
    // polls.
    staleTime: 5 * 60_000,
    retry: false,
  });

  // The ticket is minted when the fetch STARTS: a newer acquisition supersedes
  // this one even while it is still in flight, which is what lets the page's
  // check refuse a stale arrival in either completion order.
  const detonateM = useMutation({
    mutationFn: (v: { sha256: string; slot: number }) => api.intelStageSample(orgId, v.sha256),
    onSuccess: (upload, v) => onStaged(upload, { slot: v.slot, orgId }),
    onError: (e: Error) =>
      toast.error(e instanceof ApiError ? e.message : 'Could not fetch the file for analysis'),
  });

  // Running again a file this deployment already holds. Distinct from the
  // detonate above, which FETCHES a file a feed named: here the bytes are
  // already ours, so nothing is transferred and no third party is told what we
  // are interested in.
  const rerunM = useMutation({
    mutationFn: (v: { sha256: string; slot: number }) => api.restageSample(orgId, v.sha256),
    onSuccess: (upload, v) => onStaged(upload, { slot: v.slot, orgId }),
    onError: (e: Error) =>
      toast.error(e instanceof ApiError ? e.message : 'Could not stage that file for analysis'),
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const next = draft.trim();
    if (next === '') return;
    onTerm(next);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <SearchBar
        draft={draft}
        onDraft={setDraft}
        onSubmit={submit}
        searching={searchQ.isFetching}
        showHero={term === ''}
      />

      {term !== '' && searchQ.isPending && (
        <div className="card" style={{ padding: '48px 24px', textAlign: 'center' }}>
          <Loader2 className="spin" size={28} style={{ margin: '0 auto 12px', color: 'var(--accent-blue)' }} />
          <div style={{ fontWeight: 500 }}>Looking it up…</div>
          <p className="text-muted text-sm mt-2">
            Checking this deployment&rsquo;s own analyses, then external intelligence.
          </p>
        </div>
      )}

      {searchQ.isError && <SearchError error={searchQ.error} />}

      {searchQ.data && (
        <ResultCard
          detonating={detonateM.isPending}
          onDetonate={(sha256) => detonateM.mutate({ sha256, slot: acquireSlot() })}
          rerunning={rerunM.isPending}
          onRerun={(sha256) => rerunM.mutate({ sha256, slot: acquireSlot() })}
          orgId={orgId}
          result={searchQ.data}
        />
      )}
    </div>
  );
}

function SearchBar({
  draft,
  onDraft,
  onSubmit,
  searching,
  showHero,
}: {
  draft: string;
  onDraft: (v: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  searching: boolean;
  showHero: boolean;
}) {
  return (
    <div className="card" style={{ padding: showHero ? '48px 24px' : '16px' }}>
      <div style={{ maxWidth: showHero ? HERO_WIDTH : undefined, margin: showHero ? '0 auto' : undefined }}>
        {showHero && (
          <div style={{ textAlign: 'center', marginBottom: '28px' }}>
            <IntelSearchIcon className="mx-auto" size={104} />
            <h2 className="mt-4" style={{ fontSize: '1.125rem', fontWeight: 500 }}>
              Search a hash, URL, IP address or domain
            </h2>
            <p className="text-muted text-sm" style={{ margin: '8px 0 0' }}>
              Answered from this deployment&rsquo;s own analyses first, then the Foresee threat
              intelligence database. Searching sends the term, never a file.
            </p>
          </div>
        )}

        <form onSubmit={onSubmit} style={{ display: 'flex', gap: '8px' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
            <Search
              aria-hidden
              className="text-ink-500"
              size={16}
              style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }}
            />
            <input
              aria-label="Hash, URL, IP address or domain"
              autoComplete="off"
              className="input"
              onChange={(e) => onDraft(e.target.value)}
              placeholder="Hash, URL, IP address or domain"
              spellCheck={false}
              style={{ width: '100%', paddingLeft: '36px' }}
              value={draft}
            />
          </div>
          <button className="btn-primary" disabled={searching || draft.trim() === ''} type="submit">
            {searching ? <Loader2 className="spin" size={16} /> : <ArrowRight size={16} />}
            Search
          </button>
        </form>
      </div>
    </div>
  );
}

/** A refused term is the server telling the analyst what the box accepts, so
 *  its message is shown rather than replaced with a generic failure. */
function SearchError({ error }: { error: unknown }) {
  const message =
    error instanceof ApiError ? error.message : 'The search could not be completed.';
  return (
    <div
      className="card"
      style={{
        padding: '16px',
        display: 'flex',
        gap: '12px',
        background: 'var(--status-warning-bg)',
        borderColor: 'var(--status-warning)',
      }}
    >
      <AlertTriangle style={{ color: 'var(--status-warning)', flexShrink: 0 }} />
      <div>{message}</div>
    </div>
  );
}

function ResultCard({
  result,
  orgId,
  detonating,
  onDetonate,
  rerunning,
  onRerun,
}: {
  result: IntelSearchResponse;
  orgId: string;
  detonating: boolean;
  onDetonate: (sha256: string) => void;
  rerunning: boolean;
  onRerun: (sha256: string) => void;
}) {
  const sources = useMemo(() => intelSources(result), [result]);
  const headline = useMemo(() => intelHeadline(sources), [sources]);
  const line = useMemo(() => intelVerdictLine(result, headline), [result, headline]);
  const skip = skipReasonOf(result);
  // Absent on the wire rather than empty when the search resolved no file, so
  // it is normalised once here instead of at each reader.
  const sha256 = result.sample_sha256 ?? '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div className="card" style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <Identity query={result.query} resolved={sha256} />

        <div className={cn('rounded-lg border p-4', HEADLINE_TONE[line.tone])}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '220px' }}>
              <div style={{ fontWeight: 600 }}>{line.headline}</div>
              <p className="text-muted text-sm mt-1">{line.detail}</p>
              {headline.threatLabel && (
                <p className="text-accent-200 text-sm mt-2 wrap-anywhere">{headline.threatLabel}</p>
              )}
            </div>
            <SampleActions
              detonating={detonating}
              downloadable={result.downloadable}
              onDetonate={onDetonate}
              orgId={orgId}
              sha256={sha256}
            />
          </div>
        </div>
      </div>

      <LocalAnalyses
        local={result.local}
        query={result.query}
        skipped={skip !== ''}
        rerunning={rerunning}
        onRerun={onRerun}
      />

      <RecordFacts sources={sources} />
    </div>
  );
}

/** What was searched, in the exact form the feed was asked with - which is not
 *  always the form that was typed. */
function Identity({
  query,
  resolved,
}: {
  query: IntelSearchResponse['query'];
  resolved: string;
}) {
  const showResolved = resolved !== '' && resolved !== query.value;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <span className="pill border-ink-700 text-ink-300">
          {intelKindLabel(query.kind, query.digest ?? '')}
        </span>
        <code className="text-sm wrap-anywhere" style={{ flex: 1, minWidth: 0 }}>
          {query.value}
        </code>
        <CopyButton label="Copy" text={query.value} />
      </div>
      {showResolved && (
        // Only ever shown when the two differ: an MD5 search reaches this
        // platform's own analyses and a download only once the feed has named
        // the file's SHA-256, and the analyst has to see which digest the rest
        // of the card is about.
        <div className="text-muted text-sm mt-2" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>Resolved to SHA-256</span>
          <code className="wrap-anywhere" style={{ minWidth: 0 }}>{resolved}</code>
          <CopyButton label="Copy" text={resolved} />
        </div>
      )}
    </div>
  );
}

function SampleActions({
  sha256,
  orgId,
  downloadable,
  detonating,
  onDetonate,
}: {
  sha256: string;
  orgId: string;
  downloadable: boolean;
  detonating: boolean;
  onDetonate: (sha256: string) => void;
}) {
  if (!downloadable || sha256 === '') return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'stretch' }}>
      <a className="btn-secondary" href={api.intelSampleUrl(orgId, sha256)}>
        <Download size={16} />
        Download
      </a>
      <button
        className="btn-primary"
        disabled={detonating}
        onClick={() => onDetonate(sha256)}
        type="button"
      >
        {detonating ? <Loader2 className="spin" size={16} /> : <Zap size={16} />}
        Detonate
      </button>
      {/* The password is a documented constant of the provider, not a secret,
          and an analyst who cannot open the archive has been handed nothing. */}
      <p className="text-muted text-mini" style={{ textAlign: 'center', maxWidth: '18ch' }}>
        Downloads as a ZIP encrypted with <code>infected</code>
      </p>
    </div>
  );
}

function LocalAnalyses({
  local,
  query,
  skipped,
  rerunning,
  onRerun,
}: {
  local: IntelLocalAnalysis[];
  query: IntelSearchResponse['query'];
  skipped: boolean;
  rerunning: boolean;
  onRerun: (sha256: string) => void;
}) {
  if (local.length > 0) {
    // Offered for a digest, which is what this deployment can stage from its
    // own storage. A link is run from the URL console, which is a different
    // page and a different act — this card does not pretend to start one.
    const rerun =
      query.kind === 'hash' && query.value !== ''
        ? {
            label: 'Run it again',
            pending: rerunning,
            onRerun: () => onRerun(query.value),
          }
        : undefined;
    return <AnalysedHere runs={local} rerun={rerun} />;
  }

  if (query.kind === 'hash' || query.kind === 'url') {
    return (
      <div className="card" style={{ padding: '16px' }}>
        <div style={{ fontWeight: 500 }}>Never analysed here</div>
        <p className="text-muted text-sm mt-1">
          {query.kind === 'url'
            ? 'This deployment has never opened this link. Run it to produce an analysis.'
            : skipped
              ? 'This deployment has no analysis of this file.'
              : 'This deployment has no analysis of this file. Detonate it to produce one.'}
        </p>
      </div>
    );
  }

  // Said rather than left blank. An address or a name is not something this
  // platform analyses, and it keeps no index of which run contacted which host,
  // so an empty list here would report the absence of an index as the absence
  // of activity.
  return (
    <div className="card" style={{ padding: '16px' }}>
      <div style={{ fontWeight: 500 }}>No local history for a network indicator</div>
      <p className="text-muted text-sm mt-1">
        This platform detonates files and opens links. It keeps no index of which analysis
        contacted which host, so a host is checked against external intelligence only.
      </p>
    </div>
  );
}

/**
 * Everything the records say about the sample, as one list.
 *
 * Deliberately not a table of sources: which feed a fact came from is not a
 * fact about the file, and the feeds overlap enough that a per-source table
 * shows the same hash four times and reads as four findings.
 *
 * Renders only when a record exists. What the lookup did NOT cover is the card
 * above's job - it counts what answered and what was never asked.
 */
function RecordFacts({ sources }: { sources: IntelSourceResult[] }) {
  const facts = useMemo(() => mergedFacts(sources), [sources]);
  if (facts.length === 0) return null;

  return (
    <div className="card" style={{ padding: '16px' }}>
      <div style={{ fontWeight: 500, marginBottom: '12px' }}>Threat intelligence</div>
      <dl className="grid gap-x-4 gap-y-1.5" style={{ gridTemplateColumns: 'auto 1fr' }}>
        {facts.map((fact) => (
          <div className="contents" key={fact.label}>
            <dt className="text-muted text-sm">{fact.label}</dt>
            <dd className="text-sm wrap-anywhere" style={{ margin: 0 }}>
              {fact.values.join(' · ')}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

